// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

// Refer to docs/planning/USER_MANAGEMENT_UI_PLAN_COMPLETE.md
/**
 * User List Component
 * 
 * Displays users in a table format with:
 * - User information (avatar, name, email, role)
 * - Status indicators
 * - Action buttons (edit, delete, password, permissions)
 * - Pagination
 * - Bulk actions
 */

import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  Box,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  FormControl,
  InputLabel,
  Select,
  CircularProgress
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  Security as SecurityIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon
} from '@mui/icons-material';

// Import service
import userManagementService from '../../services/userManagementService';
import AlertDialog from '../common/AlertDialog';

const UserList = ({
  users,
  pagination,
  onEditUser,
  onDeleteUser,
  onPasswordManagement,
  onPageChange,
  onBulkEditComplete
}) => {
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [alertDialog, setAlertDialog] = useState({ open: false, title: '', message: '', severity: 'info' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [roleOptions, setRoleOptions] = useState([]);
  const [bulkEditRoleId, setBulkEditRoleId] = useState('');
  const [bulkEditSaving, setBulkEditSaving] = useState(false);
  const [bulkEditLoadingRoles, setBulkEditLoadingRoles] = useState(false);

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedUsers(users.map(user => user.id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleMenuOpen = (event, user) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    // Don't clear selectedUser here - it's needed for dialogs
  };

  const handleDeactivateClick = () => {
    console.log('Deactivate clicked for user:', selectedUser);
    setDeleteDialogOpen(true);
    setAnchorEl(null); // Close menu only
  };

  const handleDeactivateCancel = () => {
    console.log('Deactivate cancelled');
    setDeleteDialogOpen(false);
    setSelectedUser(null);
  };

  const handleBulkEditOpen = async () => {
    setBulkEditOpen(true);
    setBulkEditRoleId('');
    setBulkEditLoadingRoles(true);
    try {
      const rolesResponse = await userManagementService.permission.getRoles();
      const roles = Array.isArray(rolesResponse) ? rolesResponse : (rolesResponse?.roles || []);
      setRoleOptions(roles);
    } catch (err) {
      console.error('Failed to load roles:', err);
      setAlertDialog({ open: true, title: 'Error', message: 'Failed to load roles.', severity: 'error' });
    } finally {
      setBulkEditLoadingRoles(false);
    }
  };

  const handleBulkEditClose = () => {
    if (!bulkEditSaving) {
      setBulkEditOpen(false);
      setBulkEditRoleId('');
    }
  };

  const handleBulkEditSave = async () => {
    if (!bulkEditRoleId || selectedUsers.length === 0) return;
    setBulkEditSaving(true);
    try {
      for (const userId of selectedUsers) {
        await userManagementService.user.update(userId, { role_id: bulkEditRoleId });
      }
      setAlertDialog({
        open: true,
        title: 'Success',
        message: `Role updated for ${selectedUsers.length} user(s).`,
        severity: 'success'
      });
      setSelectedUsers([]);
      setBulkEditOpen(false);
      setBulkEditRoleId('');
      if (typeof onBulkEditComplete === 'function') onBulkEditComplete();
    } catch (err) {
      console.error('Bulk edit failed:', err);
      setAlertDialog({
        open: true,
        title: 'Error',
        message: err.response?.data?.detail || 'Failed to update role for one or more users.',
        severity: 'error'
      });
    } finally {
      setBulkEditSaving(false);
    }
  };

  const handleDeactivateConfirm = async () => {
    console.log('Deactivate confirm clicked for user:', selectedUser);
    if (!selectedUser) {
      console.error('No user selected for deactivation');
      return;
    }

    try {
      setDeleting(true);
      console.log('Calling deactivate API for user ID:', selectedUser.id);
      // Update user to set is_active = false
      const result = await userManagementService.user.update(selectedUser.id, { is_active: false });
      console.log('Deactivate API result:', result);
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      onDeleteUser();
    } catch (error) {
      console.error('Failed to deactivate user:', error);
      console.error('Error details:', error.response);
      setAlertDialog({
        open: true,
        title: 'Deactivation Failed',
        message: `Failed to deactivate user: ${error.response?.data?.detail || error.message}`,
        severity: 'error'
      });
    } finally {
      setDeleting(false);
    }
  };

  const handlePasswordClick = () => {
    onPasswordManagement(selectedUser);
    setAnchorEl(null); // Close menu only
  };

  const handleEditClick = () => {
    onEditUser(selectedUser);
    setAnchorEl(null); // Close menu only
    setSelectedUser(null); // Clear for edit
  };

  const getStatusChip = (user) => {
    if (user.is_locked) {
      return (
        <Chip
          icon={<WarningIcon />}
          label="Locked"
          color="error"
          variant="outlined"
          size="small"
        />
      );
    }
    
    if (user.password_must_change) {
      return (
        <Chip
          icon={<WarningIcon />}
          label="Must Change Password"
          color="warning"
          variant="outlined"
          size="small"
        />
      );
    }
    
    return user.is_active ? (
      <Chip
        icon={<CheckCircleIcon />}
        label="Active"
        color="success"
        variant="outlined"
        size="small"
      />
    ) : (
      <Chip
        icon={<CancelIcon />}
        label="Inactive"
        color="error"
        variant="outlined"
        size="small"
      />
    );
  };

  const getRoleChip = (user) => {
    const roleColors = {
      'Administrator': 'error',
      'Manager': 'warning',
      'Department Manager': 'info',
      'Analyst': 'secondary',
      'User': 'default'
    };

    return (
      <Chip
        label={user.role || 'Unknown'}
        color={roleColors[user.role] || 'default'}
        variant="outlined"
        size="small"
      />
    );
  };

  const formatLastLogin = (lastLoginAt) => {
    if (!lastLoginAt) return 'Never';
    
    const date = new Date(lastLoginAt);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    
    return date.toLocaleDateString();
  };

  return (
    <Box>
      {/* Bulk Actions */}
      {selectedUsers.length > 0 && (
        <Box sx={{ p: 2, bgcolor: 'primary.light', borderRadius: 1, mb: 2 }}>
          <Typography variant="body2" sx={{ color: 'primary.contrastText', mb: 1 }}>
            {selectedUsers.length} user{selectedUsers.length > 1 ? 's' : ''} selected
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" variant="outlined" sx={{ color: 'primary.contrastText' }} onClick={handleBulkEditOpen}>
              Bulk Edit
            </Button>
            <Button size="small" variant="outlined" sx={{ color: 'primary.contrastText' }}>
              Export Selected
            </Button>
            <Button size="small" variant="outlined" sx={{ color: 'primary.contrastText' }}>
              Send Email
            </Button>
          </Box>
        </Box>
      )}

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedUsers.length > 0 && selectedUsers.length < users.length}
                  checked={users.length > 0 && selectedUsers.length === users.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>User</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>AI Persona</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last Login</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id} hover>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedUsers.includes(user.id)}
                    onChange={() => handleSelectUser(user.id)}
                  />
                </TableCell>
                
                {/* User Info */}
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      sx={{ 
                        bgcolor: 'primary.main',
                        width: 40,
                        height: 40
                      }}
                    >
                      {user.full_name ? user.full_name.charAt(0).toUpperCase() : user.username.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {user.full_name || user.username}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <EmailIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {user.email}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </TableCell>

                {/* Role */}
                <TableCell>
                  {getRoleChip(user)}
                </TableCell>

                {/* AI Persona */}
                <TableCell>
                  {user.user_system_prompt_name ? (
                    <Chip 
                      label={user.user_system_prompt_name} 
                      size="small"
                      color="secondary"
                      variant="outlined"
                      sx={{ fontSize: '0.75rem' }}
                    />
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      Default
                    </Typography>
                  )}
                </TableCell>

                {/* Status */}
                <TableCell>
                  {getStatusChip(user)}
                </TableCell>

                {/* Last Login */}
                <TableCell>
                  <Typography variant="body2">
                    {formatLastLogin(user.last_login_at)}
                  </Typography>
                </TableCell>

                {/* Created */}
                <TableCell>
                  <Typography variant="body2">
                    {new Date(user.created_at).toLocaleDateString()}
                  </Typography>
                </TableCell>

                {/* Actions */}
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, user)}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={pagination.total}
        page={pagination.page}
        onPageChange={(event, newPage) => onPageChange(newPage)}
        rowsPerPage={pagination.limit}
        rowsPerPageOptions={[10, 25, 50, 100]}
        onRowsPerPageChange={(event) => {
          // Handle rows per page change if needed
        }}
      />

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleEditClick}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit User</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={handlePasswordClick}>
          <ListItemIcon>
            <LockIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Manage Password</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={handleDeactivateClick} sx={{ color: 'warning.main' }}>
          <ListItemIcon>
            <CancelIcon fontSize="small" sx={{ color: 'warning.main' }} />
          </ListItemIcon>
          <ListItemText>Deactivate User</ListItemText>
        </MenuItem>
      </Menu>

      {/* Deactivate Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeactivateCancel}>
        <DialogTitle>Deactivate User</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            This will deactivate the user account. You can reactivate it later.
          </Alert>
          <Typography>
            Are you sure you want to deactivate user <strong>{selectedUser?.username}</strong>?
          </Typography>
          {selectedUser && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Email:</strong> {selectedUser.email}
              </Typography>
              <Typography variant="body2">
                <strong>Full Name:</strong> {selectedUser.full_name || 'N/A'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeactivateCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeactivateConfirm}
            color="warning"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? 'Deactivating...' : 'Deactivate User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Edit Role Dialog */}
      <Dialog open={bulkEditOpen} onClose={handleBulkEditClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit role for selected users</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''} selected. Choose a new role to apply.
          </Typography>
          {bulkEditLoadingRoles ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="body2">Loading roles...</Typography>
            </Box>
          ) : (
            <FormControl fullWidth size="small">
              <InputLabel id="bulk-edit-role-label">Role</InputLabel>
              <Select
                labelId="bulk-edit-role-label"
                value={bulkEditRoleId}
                label="Role"
                onChange={(e) => setBulkEditRoleId(e.target.value)}
              >
                <MenuItem value="">
                  <em>Select role</em>
                </MenuItem>
                {roleOptions.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    {role.name || role.code || role.id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBulkEditClose} disabled={bulkEditSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleBulkEditSave}
            disabled={bulkEditSaving || !bulkEditRoleId || bulkEditLoadingRoles}
          >
            {bulkEditSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Dialog */}
      <AlertDialog
        open={alertDialog.open}
        onClose={() => setAlertDialog({ open: false, title: '', message: '', severity: 'info' })}
        title={alertDialog.title}
        message={alertDialog.message}
        severity={alertDialog.severity}
      />
    </Box>
  );
};

export default UserList;
